import { authenticatedFetch } from '../api';

export type ImpactRun = {
  id: string;
  policy_id: string;
  policy_version: number;
  sync_run_id: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  coverage_state: 'pending' | 'complete' | 'incomplete' | 'too_large' | 'failed';
  current: boolean;
  gate_passed: boolean;
  analysis_limit: number;
  planned_contexts: number;
  analyzed_contexts: number;
  changed_contexts: number;
  unchanged_contexts: number;
  conflicted_contexts: number;
  incomplete_contexts: number;
  largest_increase_pct: string | null;
  largest_decrease_pct: string | null;
  error: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
};

export type ImpactContextResult = {
  company_name: string;
  company_location_name: string;
  product_title: string;
  variant_title: string;
  sku: string | null;
  status: 'analyzed' | 'conflict' | 'incomplete';
  changed: boolean;
  conflicted: boolean;
  quantities_tested: number[];
  current_min_price: string | null;
  current_max_price: string | null;
  proposed_min_price: string | null;
  proposed_max_price: string | null;
  largest_delta_amount: string | null;
  largest_delta_pct: string | null;
  details: Record<string, unknown>;
  error: string | null;
};

export type ImpactResponse = {
  run: ImpactRun | null;
  results: ImpactContextResult[];
};

async function jsonOrError<T>(response: Response, fallback: string): Promise<T> {
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { detail?: string } | null;
    throw new Error(body?.detail ?? `${fallback} (${response.status})`);
  }
  return (await response.json()) as T;
}

export async function runImpactAnalysis(policyId: string): Promise<{
  run: ImpactRun;
  created: boolean;
  job_id: string | null;
  job_enqueued: boolean;
}> {
  const response = await authenticatedFetch(`/api/impact/policies/${policyId}/run`, {
    method: 'POST',
  });
  return jsonOrError(response, 'Could not start store-wide impact analysis');
}

export async function getLatestImpactAnalysis(policyId: string): Promise<ImpactResponse> {
  const response = await authenticatedFetch(`/api/impact/policies/${policyId}/latest`);
  return jsonOrError(response, 'Could not read store-wide impact analysis');
}

export async function getImpactAnalysis(runId: string, limit = 100): Promise<ImpactResponse> {
  const response = await authenticatedFetch(`/api/impact/runs/${runId}?limit=${limit}`);
  return jsonOrError(response, 'Could not read impact analysis details');
}
